import Vue from 'vue';
import VeeValidate from 'vee-validate';
import { i18n } from './i18n';
import { validateIban } from '@/features/profile/helpers';
import { isValidSsin } from '@/features/patients/helpers';
import { isIsoDate } from '@/utils';

// Get saved language from localStorage or default to 'nl'
const savedLocale = localStorage.getItem('locale') || 'nl';

Vue.use(VeeValidate, {
  locale: savedLocale,
  dictionary: {
    nl: {
      messages: {
        required: () => 'Verplicht veld',
        numeric: () => 'Dit veld moet een nummer zijn',
        confirmed: () => 'De waarden komen niet overeen',
        min_value: (field, [min]) => `De waarde moet minimaal ${min} zijn`,
      },
    },
    fr: {
      messages: {
        required: () => 'Champ requis',
        numeric: () => 'Ce champ doit être un nombre',
        confirmed: () => 'Les valeurs ne correspondent pas',
        min_value: (field, [min]) => `La valeur doit être au moins ${min}`,
      },
    },
    en: {
      messages: {
        required: () => 'Required field',
        numeric: () => 'This field must be a number',
        confirmed: () => 'The values do not match',
        min_value: (field, [min]) => `The value must be at least ${min}`,
      },
    },
  },
});

VeeValidate.Validator.extend('vatNumber', {
  getMessage: () => String(i18n.t('profile.vatNumberError')),
  validate: (value: string) => {
    if (!value) {
      return true;
    }

    return new RegExp('^(BE)?[0-9]{10}$').test(value);
  }
});

VeeValidate.Validator.extend('companyNumber', {
  getMessage: () => String(i18n.t('profile.companyNumberError')),
  validate: (value: string) => {
    if (!value) {
      return true;
    }

    return new RegExp('^[0-9]{10}$').test(value);
  }
});

VeeValidate.Validator.extend('iban', {
  getMessage: () => String(i18n.t('profile.invalidIban')),
  validate: async (value: string) => {
    if (!value) {
      return true;
    }

    const validationData = await validateIban(value);

    // If validation API is unavailable, allow the value
    if (!validationData) {
      return true;
    }

    return validationData.valid;
  }
});

VeeValidate.Validator.extend('validateSSIN', {
  getMessage: () => String(i18n.t('validation.ssinError')),
  validate: (value: string) => isValidSsin(value)
});

VeeValidate.Validator.extend('validateRiziv', {
  getMessage: () => String(i18n.t('validation.rizivError')),
  validate: (value: string) => {
    if (!value) {
      return true;
    }

    return /^([0-9]{8}|[0-9]{11})$/.test(value);
  }
});

VeeValidate.Validator.extend('validateDate', {
  getMessage: () => String(i18n.t('patients.dateFormatError')),
  validate: (value: string) => {
    if (!value) {
      return true;
    }

    return isIsoDate(value);
  },
});

VeeValidate.Validator.extend('validateTime', {
  getMessage: () => String(i18n.t('patients.timeFormatError')),
  validate: (value: string) => {
    if (!value) {
      return true;
    }

    return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
  },
});

// Function to update vee-validate locale when i18n locale changes
export function updateVeeValidateLocale(locale: string) {
  Vue.prototype.$validator.localize(locale);
}
